/* ═══════════════════════════════════════════════════════════════
   THE SPEC MASTERS STUDIO (docs/MASTERS_LADDER_PLAN.md §3 R2–R5).

   THE masters editor — R5 deleted BlockEditor once every item on
   docs/reviews/BLOCKEDITOR_INVENTORY.md was proven green here. Built
   entirely on the Designer's proven stack: the same TipTap schema and
   lossless ContentBlock[] converters, the same numbering brain, the same
   §2.8 panel-button system, the same universal-undo timeline, the same
   dock. The storage format never changed — a master saved here is
   identical in shape to one saved by the old editor, which is what made
   the swap safe.

   R2 scope: an office master's CORE, at full parity with BlockEditor.
   R3 adds the VARIANT BAR: Office Master + one named variant. In a
   variant the same canvas paints that variant's layer — adds,
   overrides, hides — through the R1 compose brain and `variantOps`. The
   canvas never re-implements composition, and a variant NEVER
   writes `coreContent`. Selections UI is still R4; smart blocks stay
   CUT (§0) — existing smart-token blocks render inert and survive a
   save — except Related sections (Josh 2026-10-02), which inserts from
   the Blocks palette and shows here as its placeholder.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Check, CopyPlus, Search, Trash2 } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import { docUndoDepth } from './collabUndo';
import { setRecordTitle } from '../routing/addressSync';
import type { Editor } from '@tiptap/react';
import type { ContentBlock, SpecMaster } from '../../shared/types';
import type { JSONContent } from '@tiptap/core';
import { hasPermission } from '../../shared/permissions';
import { localUpdatesNotRaised } from '../../shared/module-updates';
import { csiConflict, SpecService } from '../services/specService';
import { Prefs } from '../services/prefsService';
import { usePref } from '../lib/usePref';
import { FirmPrefs } from '../services/firmPrefsService';
import EmptyState from '../components/EmptyState';
import { EditorHistoryPanel, type EditorHistoryVersion } from '../components/EditorComponents';
import type { FormaDockHandle } from '../components/FormaDock';
import { useUndoCoordinator } from '../editor-chrome/undoCoordinator';
import {
  DesignerToolbar,
  ToolbarButton,
  type PanelEntry,
  type ToolbarItem,
} from '../editor-chrome/Toolbar';
import { InlineMarks, LevelCluster, UniversalHistory } from '../editor-chrome/ToolbarControls';
import { FindReplace } from '../designer/FindReplace';
import { closeFind, getFindState, openFind } from '../designer/search';
import { isOverDockZone, type FloatingTool } from '../editor-chrome/dock';
import '../editor-chrome/editor.css';
import '../editor-chrome/StudioShared.css';
import './Studio.css';
import { selectedBlocks } from '../designer/commands';
import { ZoomControl } from '../components/ui/ZoomControl';
import { docToBlocks } from '../designer/schema';
import { SpecStudioCanvas } from './SpecStudioCanvas';
import { VariantBar } from './VariantBar';
import { VariantStaleCompare } from './VariantStaleCompare';
import { useVariant } from './useVariant';
import { useVariantUpdates } from './useVariantUpdates';
import { UpdatesPanel } from './UpdatesPanel';
import { VARIANT_REBUILD } from './variantPlugin';
import {
  SPEC_STUDIO_FIRM_LAYOUT_KEY,
  SPEC_STUDIO_PANEL_ICONS,
  SpecStudioWorkspace,
  specStudioPalettes,
} from './SpecStudioWorkspace';
import { ModulesPanel } from '../components/ModulesPanel';
import { useModuleInsert, type ToastOptions } from '../components/useModuleInsert';
import { clearBlockSelection, setBlockSelection } from './blockSelect';
import { TrackChangesView } from './TrackChangesView';
import { SaveAsModuleDialog } from './SaveAsModuleDialog';
import { ModuleBuilder, type ModuleBuilderDraft } from './ModuleBuilder';
import { useSpecMasterDraft, type StudioMode } from './useSpecMasterDraft';
import { PeerChips } from './PeerChips';
import { ViewModeContext, guardHandler, type ViewModeValue } from './useViewModeGuard';
import {
  discardConfirmBody,
  draftPresenceLabel,
  liveBlockMessage,
  masterStatusChip,
  studioChipLabel,
  type LiveBlock,
  type StudioChipState,
} from './collabState';
import { Hint } from '../components/ui/tooltip';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { InfoHint } from './InfoHint';
import { PublishDialog } from './PublishDialog';
import { DraftPendingDialog } from './DraftPendingDialog';
import { ExportMastersDialog } from '../pages/ExportMastersDialog';

/** Masters whose draft-pending notice already showed this app session. */
const draftNoticeShown = new Set<string>();
import { publishErrorMessage, sameOffers, type PublishPlan } from './publishPlan';
import { diffOutlineBlocks, trackChangesDiff, trackChangesSides } from './trackChangesSource';
import { ReviewBar } from '../review/ReviewBar';
import { ReviewPanel } from '../review/ReviewPanel';
import { usePublishWalk } from '../review/usePublishWalk';
import { revertLaneTitle, useRevertReview } from '../review/useRevertReview';
import { revertHistoryNote } from '../review/revertReview';
import { editorBoundTo } from './collabBinding';
import { useReviewDecorations } from '../review/useReviewDecorations';
import { useVariantReview } from './useVariantReview';
import { useChangeWalker } from '../review/useReview';
import type { ReviewPluginInput } from '../review/reviewPlugin';
import type { Change } from '../../shared/sync';

/** Stable empty list for the variant Review walker. */
const NO_REVIEW_ITEMS: Change[] = [];

// Cohesion R1 Phase 4 zoom law: 50–180% everywhere.
// UI Scale (DESIGN.md §3.6): `zoom` here is RELATIVE to the dial (1.0 =
// "matches surroundings") — the canvas multiplies it by the dial to get
// the applied transform. Storage/keys are unchanged: every value saved so
// far was captured at dial 1.00, so a saved number IS the relative value
// already — no migration/conversion needed.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.8;
const ZOOM_KEY = 'specStudio.zoom';
const VIEW_KEY = 'sf.specStudioViewOpts.v1';

/** Automated capture/QA runs sign in as a real user; without this they would
    overwrite that user's CLOUD zoom with a throwaway one (FormaDock.tsx has
    the same guard for dock layouts). Local persistence still works so a
    harness can test restore within its own profile. */
const cloudPersistDisabled = () => {
  try {
    return localStorage.getItem('sf.dockNoCloudPersist') === '1';
  } catch {
    return false;
  }
};

/* ─── View options (BlockEditor T3, minus the variant toggles that
       REMAP onto variants in R3). Purely visual; no saved data changes. ─── */
interface ViewOpts {
  showExcluded: boolean;
  showParagraphNumbers: boolean;
  /** R3: the dashed stubs a variant leaves where it hid a block. */
  showGhosts: boolean;
  /** R3: the "‹Client›" / "⇄" / "Overridden" tags. The coloured edge stays
   *  either way — provenance is never invisible, only quieter. */
  showProvenance: boolean;
}
const DEFAULT_VIEW_OPTS: ViewOpts = {
  showExcluded: true,
  showParagraphNumbers: true,
  showGhosts: true,
  showProvenance: true,
};

function parseViewOpts(raw: string): ViewOpts {
  return { ...DEFAULT_VIEW_OPTS, ...JSON.parse(raw) };
}

/** The one save chip of a live shared draft: Connecting… / Saved /
 *  Saving… / Offline. */
function SaveIndicator({ state }: { state: StudioChipState }) {
  const tone =
    state === 'saved' ? 'clean' : state === 'saving' || state === 'connecting' ? 'saving' : 'dirty';
  return (
    <span className={`dsg-save dsg-save-${tone}`} role="status" aria-live="polite">
      {state === 'saved' && <Icon icon={Check} em={1.0909} aria-hidden="true" />}
      {studioChipLabel(state)}
    </span>
  );
}

/* ─── Paragraph toolbar group: the level cluster only (Modules R2 Phase 5,
       Ruling 9). Continuation, Skip at export and Save as module moved to
       the Blocks toolbox (Cohesion R1 Phase 4, Studio top-bar ruling) —
       SpecStudioWorkspace.tsx's BlocksPalette. Their shortcuts (Ctrl+Shift+C)
       and SpecBlockMenu context-menu entries are untouched; only their
       toolbar home changed. ─── */
function ParagraphGroup({ editor, canEdit }: { editor: Editor | null; canEdit: boolean }) {
  return (
    <div className="sps-para-group">
      <LevelCluster editor={editor} canEdit={canEdit} />
    </div>
  );
}

/* ─── A small modal, portal-free (it lives inside the Studio root, which
       is the full page) with focus trap + Esc + return-focus (DESIGN §7). ─── */
function Modal({
  title,
  sub,
  onClose,
  children,
}: {
  title: string;
  sub?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('input, button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      const items = [
        ...ref.current.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)'),
      ];
      if (items.length < 2) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      returnTo?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="dsg-modal-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dsg-modal" ref={ref}>
        <h2 className="dsg-modal-title">{title}</h2>
        {sub && <p className="dsg-modal-sub">{sub}</p>}
        {children}
      </div>
    </div>
  );
}

export interface SpecStudioProps {
  masterId: string;
  onBack: () => void;
  /** The app toast; the optional third argument carries an action button
   *  (a module drop's "Show" — app.tsx `toast` already accepts it). */
  toast: (msg: string, isError?: boolean, opts?: ToastOptions) => void;
  userPermissions?: string[];
  userName: string;
  userId?: string;
  isDark?: boolean;
  /** Save As created a new master — open that one instead. */
  onSavedAs?: (newId: string) => void;
  /** Mirrors dirty state into the shell's exit guard. */
  parentDirtyRef?: React.MutableRefObject<boolean>;
  /** The shell raises the Studio's own Save / Discard / Keep-editing dialog
   *  through this when a nav click or an OS close lands on a dirty draft —
   *  the same contract BlockEditor honours, so no work is ever lost. */
  exitGuardTriggerRef?: React.MutableRefObject<{ show: () => void } | null>;
  /** "Keep editing" was chosen — the shell drops its pending destination. */
  onCancelExit?: () => void;
  /** The native File-menu Ctrl+S accelerator routes through this. */
  registerSaveHandler?: (fn: (() => void) | null) => void;
  /** Open with the publish dialog up — a review-queue Approve (the ONE
   *  publish path, SPEC_WORKFLOW §7). */
  openPublish?: boolean;
  /** The pre-opened publish was started — the shell clears its flag. */
  onPublishOpened?: () => void;
}

export function SpecStudio({
  masterId,
  onBack,
  toast,
  userPermissions = [],
  userName,
  userId,
  isDark = false,
  onSavedAs,
  parentDirtyRef,
  exitGuardTriggerRef,
  onCancelExit,
  registerSaveHandler,
  openPublish = false,
  onPublishOpened,
}: SpecStudioProps) {
  const canDirectPublish = hasPermission(userPermissions, 'specs.publish');
  const canDelete = hasPermission(userPermissions, 'specs.delete');
  // 'specs.edit' is a retired pre-split key (see shared/permissions.ts
  // LEGACY_KEY_EXPANSIONS) — the client doesn't expand legacy keys, so an
  // Editor holding only specs.edit_own used to get canEdit === false.
  // Same writer rule as the server's collab gate: the legacy `specs.edit`
  // key counts too (a holder of only that key could otherwise be picked as
  // the seeder while their canvas stayed read-only).
  const canEdit =
    hasPermission(userPermissions, 'specs.edit') ||
    hasPermission(userPermissions, 'specs.edit_own') ||
    hasPermission(userPermissions, 'specs.edit_any') ||
    canDirectPublish;
  // SPEC_WORKFLOW §4 — opening a master opens VIEW (the published text,
  // read-only, for everyone); Edit enters the shared draft. State, not a route.
  const [mode, setMode] = React.useState<StudioMode>('view');
  const editing = mode === 'edit' && canEdit;
  // Live shared draft: the server is the gate (view to open, edit to write);
  // `canEdit` only decides whether this window may seed the document.
  const draft = useSpecMasterDraft(masterId, userName, toast, { canEdit, mode });
  // Live editing refused for good (auth / origin / bad request / too many /
  // denied): ONE error toast, back to View, and Edit says why until a
  // reconnect syncs. Never a frozen Edit stuck on a chip.
  const liveBlocked = draft.liveBlocked;
  const liveBlockedMessage = liveBlocked ? liveBlockMessage(liveBlocked) : null;
  const toastedBlockRef = React.useRef<LiveBlock | null>(null);
  React.useEffect(() => {
    if (!liveBlocked) {
      toastedBlockRef.current = null;
      return;
    }
    if (toastedBlockRef.current === liveBlocked) return;
    toastedBlockRef.current = liveBlocked;
    if (canEdit) toast(liveBlockMessage(liveBlocked), true);
    setMode('view');
  }, [liveBlocked, canEdit, toast]);
  const enterEdit = React.useCallback(() => {
    if (liveBlocked) {
      toast(liveBlockMessage(liveBlocked), true);
      return;
    }
    setMode('edit');
  }, [liveBlocked, toast]);
  const [draftNoticeOpen, setDraftNoticeOpen] = React.useState(false);
  const [exportOpen, setExportOpen] = React.useState(false);
  const canExport = hasPermission(userPermissions, 'specs.export');
  React.useEffect(() => {
    if (mode !== 'view' || !canDirectPublish) return;
    if (draft.loadState !== 'ready' || !draft.draftInProgress) return;
    if (draftNoticeShown.has(masterId)) return;
    draftNoticeShown.add(masterId);
    setDraftNoticeOpen(true);
  }, [mode, canDirectPublish, draft.loadState, draft.draftInProgress, masterId]);
  // View mode nudges toward Edit: a press on any edit-only control pulses the
  // Edit button once (class toggled on the node, removed on animationend).
  const editBtnRef = React.useRef<HTMLButtonElement>(null);
  const nudgeEdit = React.useCallback(() => {
    if (!canEdit) return;
    const el = editBtnRef.current;
    if (!el) return;
    el.classList.remove('sps-edit-nudge');
    void el.offsetWidth; // reflow so a second press restarts the ring
    el.classList.add('sps-edit-nudge');
  }, [canEdit]);
  // ─── Publish self-review (SYNC_PLAN D3) ───
  // Publish first WALKS this draft against the published version (Previous /
  // Next / Continue — nothing to decide), painted on the canvas by the same
  // Review plugin a project uses. Continue opens today's PublishDialog. A
  // local flag, not a StudioMode: the walk lives inside Edit (the shared
  // draft stays connected) and useSpecMasterDraft keys behaviour off mode.
  const [editor, setEditor] = React.useState<Editor | null>(null);
  const {
    walk,
    setWalk,
    walker,
    start: startWalk,
    finish: finishWalk,
    leave: leaveWalk,
    input: walkInput,
  } = usePublishWalk(editor, { paint: false });
  // ─── Revert as a review (Tweaks 36) ───
  // History → Revert opens the Review lane over the shared draft: every
  // paragraph that would change back is a pending mark. Apply revert lands
  // the accepted mix in the DRAFT; Publish stays the only road out.
  const revertReview = useRevertReview(editor);
  const reverting = revertReview.active;
  const cancelRevert = revertReview.cancel;
  React.useEffect(() => {
    if (mode !== 'edit') {
      setWalk(null);
      cancelRevert();
    }
  }, [mode, setWalk, cancelRevert]);
  const vm = React.useMemo<ViewModeValue>(
    () =>
      walk || reverting
        ? {
            editing: false,
            canEdit,
            nudgeEdit: () => undefined,
            reason: walk ? 'Continue or leave Review to edit' : 'Apply revert or cancel to edit',
          }
        : { editing, canEdit, nudgeEdit },
    [walk, reverting, editing, canEdit, nudgeEdit]
  );
  const canSetFirmDefault = hasPermission(userPermissions, 'admin_center.manage_firm_settings');
  // Phase 3 — deciding an office-master update on a variant is its
  // own responsibility, server-enforced on both decide endpoints.
  const canAcceptUpdates = hasPermission(userPermissions, 'specs.accept_updates');

  const editorRef = React.useRef<Editor | null>(null);
  editorRef.current = editor;
  const toolbarRef = React.useRef<HTMLDivElement>(null);

  // ─── Save as module (Phase 4) ───
  // A module is a reusable BLOCK-GROUP (never a variant). Capturing one is a
  // pure read of the open document, so it works in a read-only master too —
  // nothing here writes to the spec. `modules.create` gates the affordance
  // client-side; the server gates the row write with the same key.
  const canCreateModule = hasPermission(userPermissions, 'modules.create');
  // Phase 6 — replacing a definition (and raising its update PRs) is
  // `modules.edit`, server-enforced on both the row update and the raise.
  const canEditModules = hasPermission(userPermissions, 'modules.edit');
  const [moduleCapture, setModuleCapture] = React.useState<{
    blocks: ContentBlock[];
    ids: string[];
  } | null>(null);
  const openSaveAsModule = React.useCallback(() => {
    const ed = editorRef.current;
    if (!ed) return;
    const ids = selectedBlocks(ed.state)
      .map((b) => b.node.attrs.blockId as string)
      .filter(Boolean);
    if (!ids.length) {
      toast('Select the blocks you want first — click the grips in the left gutter', true);
      return;
    }
    // The document as it stands on screen — the same shape the canvas hands
    // back on every change, so chunking and anchors read what the user sees.
    setModuleCapture({ blocks: docToBlocks(ed.state.doc.toJSON()), ids });
  }, [toast]);

  // R2 Phase 4: "Save as module" lands in the MODULE BUILDER, where the parts
  // are assigned and the structure law is satisfied before anything is
  // written. It takes the whole Studio while it is open (the module is the
  // hero then, DESIGN §3.4) — and the gutter selection is dropped first,
  // because those grips portal to <body> and would otherwise float over it.
  const [moduleBuilder, setModuleBuilder] = React.useState<ModuleBuilderDraft | null>(null);
  const openModuleBuilder = React.useCallback((next: ModuleBuilderDraft) => {
    const ed = editorRef.current;
    if (ed) clearBlockSelection(ed.state, ed.view.dispatch.bind(ed.view));
    setModuleBuilder(next);
  }, []);

  // ─── Insert a module (Phase 5) ───
  // Inserting rides the TARGET's edit gate — a module is only ever a copy of
  // blocks, so there is no second permission for dropping one in. Seeing the
  // library is `modules.view`; without it the panel is never offered and the
  // list is never fetched.
  const canViewModules = hasPermission(userPermissions, 'modules.view');
  const pickBlocks = React.useCallback((ids: string[]) => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    // The gutter selection layer (Phase 1 item 4) — so "Select insertion"
    // names every block of the insertion at once, contiguous or not, and every
    // existing block command then acts on exactly that set.
    setBlockSelection(ed.state, ed.view.dispatch.bind(ed.view), ids, ids[0] ?? null);
    ed.commands.focus();
  }, []);
  // ─── Variants (R3): Office Master + one named layer per variant ───
  const getEditor = React.useCallback(() => editorRef.current, []);
  const variant = useVariant(masterId, draft, getEditor, toast);

  const moduleInsert = useModuleInsert({
    editor,
    canEdit: editing,
    canView: canViewModules,
    // Masters render on the app-standard CSI ladder — there is no forma until
    // a spec reaches a project (SpecStudioCanvas's header).
    forma: null,
    toast,
    selectBlocks: pickBlocks,
    // R2 Phase 3: a module standing in this document can be told the library
    // moved, and answered per paragraph in the Updates panel. Only on the
    // OFFICE MASTER: inside a variant the sheet is a COMPOSED copy, so an
    // answer would land as a per-variant override and the re-stamp — which is
    // not content — would not persist at all. A variant's own
    // insertions are covered by the raised feed (Phase 6) instead.
    updatesEnabled: !variant.inVariant,
    specMasterId: masterId,
  });
  const [deleteVariantTarget, setDeleteVariantTarget] = React.useState<string | null>(null);

  // View ⇄ Edit swaps what the sheet shows (published ⇄ draft): recompose
  // the variant on screen from the new source. Skips the first render — the
  // variant hook places the remembered variant itself.
  const shownModeRef = React.useRef(mode);
  const switchVariant = variant.switchTo;
  const activeVariantId = variant.activeLayerId;
  React.useEffect(() => {
    if (shownModeRef.current === mode) return;
    shownModeRef.current = mode;
    switchVariant(activeVariantId);
  }, [mode, switchVariant, activeVariantId]);

  // ─── Updates from the office master (Phase 3) ───
  // A decision rewrites the variant's layer, so the sheet is recomposed the
  // moment one lands — the same rebuild a pick used to ask for.
  const recomposeVariant = variant.recompose;
  const updates = useVariantUpdates(
    masterId,
    draft,
    variant.activeLayerId,
    canAcceptUpdates,
    toast,
    recomposeVariant
  );
  // The derived module updates (R2 Phase 3) ride the same panel and the same
  // badge as the raised feed — one place to look, whoever asked the question.
  // Sync D7: an insertion a raised module entry already offers is not asked
  // twice — the raised entry wins.
  const localUpdates = React.useMemo(
    () => localUpdatesNotRaised(moduleInsert.localUpdates, updates.entries),
    [moduleInsert.localUpdates, updates.entries]
  );
  const feedEntries = React.useMemo(
    () => [...updates.entries, ...localUpdates],
    [updates.entries, localUpdates]
  );
  const localUpdateCount = React.useMemo(
    () =>
      localUpdates.reduce(
        (n, e) => n + e.changes.filter((c) => c.decision === 'pending').length,
        0
      ),
    [localUpdates]
  );

  // Tweaks 35: a variant's pending master updates are painted IN the sheet
  // with the Review engine; the publish walk takes the paint while it runs.
  const activeVariantLayer = React.useMemo(
    () => draft.layers.find((l) => l.id === variant.activeLayerId) || null,
    [draft.layers, variant.activeLayerId]
  );
  const variantReview = useVariantReview({
    editor,
    masterId,
    publishedCore: draft.master?.coreContent,
    layer: activeVariantLayer,
    entries: updates.entries,
    pendingIds: updates.pendingIds,
    version: updates.version,
  });
  // The Review bar walks one stop per pending block; its answers go through
  // the SAME decide as the Updates panel and the block menu.
  const variantWalker = useChangeWalker(variantReview?.items ?? NO_REVIEW_ITEMS);
  const [variantBarClosed, setVariantBarClosed] = React.useState(false);
  React.useEffect(() => setVariantBarClosed(false), [variant.activeLayerId]);
  const variantCurrent = variantWalker.current;
  const variantReviewInput = React.useMemo<ReviewPluginInput | null>(
    () =>
      variantReview && variantReview.paint.length
        ? {
            changes: variantReview.paint,
            decisions: {},
            currentId: variantBarClosed ? null : (variantCurrent?.id ?? null),
          }
        : null,
    [variantReview, variantCurrent, variantBarClosed]
  );
  useReviewDecorations(editor, walkInput ?? variantReviewInput);
  const decideVariantBlock = updates.decideBlock;
  const decideVariantAll = updates.decideAll;
  const variantEntries = updates.entries;
  const acceptAllVariant = React.useCallback(async () => {
    for (const e of variantEntries) {
      if (e.moduleId || e.local || e.layerId !== variant.activeLayerId) continue;
      if (e.changes.some((c) => c.decision === 'pending')) await decideVariantAll(e.id, 'accepted');
    }
  }, [variantEntries, variant.activeLayerId, decideVariantAll]);

  const getPendingUpdateIds = React.useCallback(() => updates.pendingIds, [updates.pendingIds]);
  const updatesVersion = updates.version;

  // The update tags are decorations built from state OUTSIDE the document, so
  // the sheet has to be asked to repaint when the feed moves (loads, or a
  // decision lands) — the same one-more-pass the variant derive asks for.
  React.useEffect(() => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    ed.view.dispatch(ed.state.tr.setMeta(VARIANT_REBUILD, true).setMeta('addToHistory', false));
  }, [updatesVersion, editor]);

  /** The canvas hands over BOTH shapes; where they land depends on the variant.
   *  Office Master → `coreContent`. A variant → that variant's layer, and
   *  never the core (§2.2). */
  const onCanvasChange = React.useCallback(
    (blocks: ContentBlock[], doc: JSONContent) => {
      if (variant.inVariant) variant.applyDoc(doc);
      else draft.onBlocksChanged(blocks);
    },
    [variant, draft]
  );

  // ─── Universal undo (§2.7): ONE Ctrl+Z timeline for the whole surface ───
  const undoTimeline = useUndoCoordinator();
  React.useEffect(() => {
    if (!editor) return;
    undoTimeline.register('doc', {
      undo: () => editor.chain().focus().undo().run(),
      redo: () => editor.chain().focus().redo().run(),
    });
    let depth = docUndoDepth(editor.state);
    const onTx = () => {
      const next = docUndoDepth(editor.state);
      if (next > depth) undoTimeline.note('doc');
      depth = next;
    };
    editor.on('transaction', onTx);
    return () => {
      editor.off('transaction', onTx);
      undoTimeline.register('doc', null);
    };
  }, [editor, undoTimeline]);
  const resetTimeline = undoTimeline.reset;
  React.useEffect(() => resetTimeline(), [masterId, resetTimeline]);

  // ─── Dirty mirroring for the shell's exit guard ───
  React.useEffect(() => {
    if (parentDirtyRef) parentDirtyRef.current = draft.unsaved;
  }, [draft.unsaved, parentDirtyRef]);

  // ─── Save wiring (Ctrl+S here and from the native File menu) ───
  const handleSave = React.useCallback(() => void draft.save(), [draft]);
  const handleSaveRef = React.useRef(handleSave);
  handleSaveRef.current = handleSave;
  React.useEffect(() => {
    registerSaveHandler?.(() => handleSaveRef.current());
    return () => registerSaveHandler?.(null);
  }, [registerSaveHandler]);

  // Esc always closes the find bar, wherever focus happens to be (DESIGN §7).
  // It matters after "Replace all": the last match is gone, so the button the
  // user just pressed disables itself and focus falls to the body — the bar's
  // own key handler would never see the keystroke.
  React.useEffect(() => {
    if (!editor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!getFindState(editor.state)?.open) return;
      e.preventDefault();
      e.stopPropagation();
      closeFind(editor.state, editor.view.dispatch.bind(editor.view));
      editor.commands.focus();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [editor]);

  // Ctrl+Z / Ctrl+Y outside the document (a toolbar button has focus, say)
  // must still run the ONE timeline.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (/^(input|textarea)$/i.test(t.tagName) || t.isContentEditable)) return;
      const key = e.key.toLowerCase();
      if (key === 's') {
        e.preventDefault();
        handleSaveRef.current();
        return;
      }
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = key === 'y' || (key === 'z' && e.shiftKey);
      if (!isUndo && !isRedo) return;
      e.preventDefault();
      if (isUndo) undoTimeline.undo();
      else undoTimeline.redo();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [undoTimeline]);

  // ─── View options (D1 cross-surface parity — cloud `user_prefs`,
  // localStorage fast cache, same key doubling as both pref + local key) ───
  const [viewOpts, setViewOpts] = usePref<ViewOpts>(VIEW_KEY, VIEW_KEY, DEFAULT_VIEW_OPTS, {
    parse: parseViewOpts,
  });
  const [viewMenu, setViewMenu] = React.useState<{ left: number; bottom: number } | null>(null);

  // ─── Track changes (SPEC_WORKFLOW §6): this draft vs the published version ───
  // DERIVED from the live document and recomputed on every change while it
  // is open (debounced) — never a snapshot. In a variant it compares the
  // composed variant, so a layer edit shows.
  const [trackOpen, setTrackOpen] = React.useState(false);
  const toggleTrackChanges = React.useCallback(() => setTrackOpen((v) => !v), []);
  React.useEffect(() => {
    if (mode !== 'edit') setTrackOpen(false);
  }, [mode]);
  const [diffRev, bumpDiff] = React.useReducer((n: number) => n + 1, 0);
  const sharedDoc = draft.collab?.ydoc ?? null;
  React.useEffect(() => {
    if (!trackOpen || !sharedDoc) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const onUpdate = () => {
      if (t) clearTimeout(t);
      t = setTimeout(bumpDiff, 300);
    };
    sharedDoc.on('update', onUpdate);
    return () => {
      sharedDoc.off('update', onUpdate);
      if (t) clearTimeout(t);
    };
  }, [trackOpen, sharedDoc]);
  const publishedMaster = draft.master;
  const draftLayers = draft.layers;
  const readDraftBlocks = draft.currentBlocks;
  const diffBlocks = React.useMemo(
    () =>
      trackOpen && publishedMaster
        ? trackChangesDiff({
            masterId,
            publishedCore: publishedMaster.coreContent || [],
            publishedLayers: publishedMaster.layers || [],
            draftCore: readDraftBlocks(),
            draftLayers,
            layerId: variant.activeLayerId,
          })
        : null,
    // `diffRev` is the shared document's revision — the real dependency of
    // `readDraftBlocks()`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      trackOpen,
      diffRev,
      publishedMaster,
      draftLayers,
      readDraftBlocks,
      variant.activeLayerId,
      masterId,
    ]
  );
  const outlineBlocks = React.useMemo(
    () => (diffBlocks ? diffOutlineBlocks(diffBlocks) : null),
    [diffBlocks]
  );
  const jumpToDiffRow = React.useCallback((blockId: string) => {
    const row = document.querySelector<HTMLElement>(`[data-diff-id="${CSS.escape(blockId)}"]`);
    row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, []);

  // ─── Zoom (per user, synced) ───
  const validZoom = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) && n >= ZOOM_MIN && n <= ZOOM_MAX ? n : null;
  };
  const [zoom, setZoom] = React.useState<number>(() => {
    let local: string | null = null;
    try {
      local = localStorage.getItem(ZOOM_KEY);
    } catch {
      /* private mode */
    }
    return validZoom(local) ?? validZoom(Prefs.get(ZOOM_KEY)) ?? 1;
  });
  const setZoomPersisted = (next: number) => {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Number(next.toFixed(2))));
    setZoom(clamped);
    if (!cloudPersistDisabled()) Prefs.set(ZOOM_KEY, clamped);
    try {
      localStorage.setItem(ZOOM_KEY, String(clamped));
    } catch {
      /* private mode */
    }
  };

  // ─── §2.8 panels: click = anchored dropdown, drag = dock or float ───
  const dockHandleRef = React.useRef<FormaDockHandle | null>(null);
  const [dockPanels, setDockPanels] = React.useState<string[]>([]);
  const [floatingTools, setFloatingTools] = React.useState<FloatingTool[]>([]);
  // The publish walk is its own screen (T4): docks + floating tools step
  // aside, the saved layout is held untouched, and everything returns after.
  const walking = !!walk || reverting;
  const floatingStashRef = React.useRef<FloatingTool[] | null>(null);
  const floatingNowRef = React.useRef(floatingTools);
  floatingNowRef.current = floatingTools;
  const [dockReady, setDockReady] = React.useState(false);
  React.useEffect(() => {
    if (!walking || !dockReady) return;
    dockHandleRef.current?.suspend();
    floatingStashRef.current = floatingNowRef.current;
    setFloatingTools([]);
    return () => {
      dockHandleRef.current?.resume();
      const stash = floatingStashRef.current;
      floatingStashRef.current = null;
      if (stash) setFloatingTools(stash);
    };
  }, [walking, dockReady]);
  const [dropdown, setDropdown] = React.useState<{
    id: string;
    anchor: { left: number; bottom: number };
  } | null>(null);
  const [panelDragId, setPanelDragId] = React.useState<string | null>(null);

  React.useEffect(() => {
    setFloatingTools((f) => {
      const next = f.filter((t) => !dockPanels.includes(t.id));
      return next.length === f.length ? f : next;
    });
    setDropdown((d) => (d && dockPanels.includes(d.id) ? null : d));
  }, [dockPanels]);

  const floatTool = React.useCallback((id: string, at?: { left: number; top: number } | null) => {
    setDropdown((d) => (d?.id === id ? null : d));
    setFloatingTools((f) => (f.some((t) => t.id === id) ? f : [...f, { id, at }]));
  }, []);
  const closeFloating = React.useCallback(
    (id: string) => setFloatingTools((f) => f.filter((t) => t.id !== id)),
    []
  );
  const dockFloating = React.useCallback((id: string) => {
    setFloatingTools((f) => f.filter((t) => t.id !== id));
    dockHandleRef.current?.showPanel(id);
  }, []);
  const onPanelClick = React.useCallback(
    (id: string, anchor: { left: number; bottom: number }) => {
      if (dockPanels.includes(id)) {
        dockHandleRef.current?.showPanel(id);
        return;
      }
      if (floatingTools.some((t) => t.id === id)) {
        closeFloating(id);
        return;
      }
      setDropdown((d) => (d?.id === id ? null : { id, anchor }));
    },
    [dockPanels, floatingTools, closeFloating]
  );
  const onPanelDragEnd = React.useCallback(
    (id: string, x: number, y: number) => {
      setPanelDragId(null);
      if (isOverDockZone(x, y)) return;
      floatTool(id, { left: x, top: y });
    },
    [floatTool]
  );

  const [dockLocked, setDockLocked] = usePref<boolean>(
    'sf.specStudioDockLocked',
    'sf.specStudioDockLocked',
    true,
    { parse: (raw) => raw !== '0', serialize: (v) => (v ? '1' : '0') }
  );
  const toggleDockLocked = React.useCallback(() => setDockLocked((v) => !v), [setDockLocked]);

  const setFirmDefault = React.useCallback(() => {
    const layout = dockHandleRef.current?.snapshotLayout();
    if (!layout) {
      toast('The workspace is still opening — try again in a moment.', true);
      return;
    }
    FirmPrefs.set(SPEC_STUDIO_FIRM_LAYOUT_KEY, layout, userName)
      .then(() =>
        toast('Saved as the firm default — everyone who has not customised will see this layout')
      )
      .catch((err: unknown) =>
        toast(
          'Could not save the firm default: ' +
            (err instanceof Error ? err.message : String(err ?? 'unknown')),
          true
        )
      );
  }, [toast, userName]);

  const panelEntries = React.useMemo<PanelEntry[]>(
    () =>
      specStudioPalettes(canViewModules)
        .filter((p) => p.id !== 'canvas')
        .map((p) => ({
          id: p.id,
          title: p.title,
          icon: SPEC_STUDIO_PANEL_ICONS[p.id] ?? null,
          where: dockPanels.includes(p.id)
            ? 'docked'
            : floatingTools.some((t) => t.id === p.id)
              ? 'floating'
              : dropdown?.id === p.id
                ? 'dropdown'
                : null,
          // Two panels carry a count, and only when something waits: Updates
          // (office-master changes to answer) and Modules (chunks with no spot
          // yet — they die with the session, so they must be visible).
          ...(p.id === 'updates' && updates.pendingTotal + localUpdateCount > 0
            ? { badge: updates.pendingTotal + localUpdateCount }
            : {}),
          ...(p.id === 'modules' && moduleInsert.chips.length > 0
            ? { badge: moduleInsert.chips.length }
            : {}),
        })),
    [
      dockPanels,
      floatingTools,
      dropdown,
      updates.pendingTotal,
      localUpdateCount,
      canViewModules,
      moduleInsert.chips,
    ]
  );

  /** An "Update" tag on a block was clicked: show the panel wherever it lives
   *  and scroll its card into view. */
  const openUpdatesFor = React.useCallback(
    (blockId: string, anchor: DOMRect) => {
      updates.openForBlock(blockId);
      if (dockPanels.includes('updates')) {
        dockHandleRef.current?.showPanel('updates');
        return;
      }
      if (floatingTools.some((t) => t.id === 'updates')) return;
      setDropdown({ id: 'updates', anchor: { left: anchor.left, bottom: anchor.bottom } });
    },
    [updates, dockPanels, floatingTools]
  );

  /* One feed, two sources: the entries the server RAISED when a master
     published or a definition moved, and the ones derived right here because
     the library is ahead of a module standing in this document (R2 Phase 3).
     They share every card, so the reader never learns two vocabularies. */
  /** An "update available" badge in the Modules panel was clicked: build (or
   *  re-open) that insertion's update and show the panel next to the badge. */
  const openUpdatesForInsertion = React.useCallback(
    (insertId: string, anchor: DOMRect) => {
      moduleInsert.reviewInsertion(insertId);
      if (dockPanels.includes('updates')) {
        dockHandleRef.current?.showPanel('updates');
        return;
      }
      if (floatingTools.some((t) => t.id === 'updates')) return;
      setDropdown({ id: 'updates', anchor: { left: anchor.left, bottom: anchor.bottom } });
    },
    [moduleInsert, dockPanels, floatingTools]
  );

  const updatesPanel = (
    <UpdatesPanel
      intro={
        <div className="sps-pal-intro">
          <span>Offers from newer masters and modules</span>
          <InfoHint
            label="Updates"
            text="Changes offered to this document by its master or by re-saved modules. Accept or keep yours."
          />
        </div>
      }
      clientName={variant.inVariant ? variant.activeLayer?.name || 'This variant' : null}
      entries={feedEntries}
      canDecide={canAcceptUpdates}
      busy={updates.busy}
      focusChangeId={updates.focusChangeId ?? moduleInsert.focusLocalChangeId}
      onDecide={(entryId, changeId, decision) => {
        if (moduleInsert.ownsEntry(entryId)) moduleInsert.decideLocal(entryId, changeId, decision);
        else void updates.decide(entryId, changeId, decision);
      }}
      onDecideAll={(entryId, decision) => {
        if (moduleInsert.ownsEntry(entryId)) moduleInsert.decideLocalAll(entryId, decision);
        else void updates.decideAll(entryId, decision);
      }}
    />
  );

  const modulesPanel = canViewModules ? (
    <ModulesPanel
      modules={moduleInsert.modules}
      loading={moduleInsert.loading}
      error={moduleInsert.error}
      canInsert={editing}
      insertGuard={{
        inert: !editing,
        reason: canEdit ? 'Click Edit to make changes' : 'You have view access only',
        onBlocked: nudgeEdit,
      }}
      busyModuleId={moduleInsert.busyModuleId}
      onInsert={moduleInsert.insertModule}
      onRetry={moduleInsert.reload}
      userName={userName}
      userPermissions={userPermissions}
      toast={toast}
      chips={moduleInsert.chips}
      armedKey={moduleInsert.armedKey}
      onArm={moduleInsert.arm}
      onDiscard={moduleInsert.discard}
      inDocument={moduleInsert.inDocument}
      onReveal={moduleInsert.revealInsertion}
      revealInsertId={moduleInsert.panelReveal?.insertId ?? null}
      revealSeq={moduleInsert.panelReveal?.seq ?? 0}
      onRemove={moduleInsert.requestRemoveByInsertId}
      onReview={openUpdatesForInsertion}
    />
  ) : null;

  // ─── Modals ───
  const [saveAsOpen, setSaveAsOpen] = React.useState(false);
  const [saveAsTitle, setSaveAsTitle] = React.useState('');
  const [saveAsCsi, setSaveAsCsi] = React.useState('');
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [publishMessage, setPublishMessage] = React.useState('');
  const [leaveOpen, setLeaveOpen] = React.useState(false);
  const [discardOpen, setDiscardOpen] = React.useState(false);
  const [discardBusy, setDiscardBusy] = React.useState(false);
  /** History → Revert on this version: opens the Revert lane once the canvas
   *  is the shared draft (Edit, Office Master tab, bound). */
  const [pendingRevert, setPendingRevert] = React.useState<number | null>(null);
  const [publishPlan, setPublishPlan] = React.useState<PublishPlan | null>(null);
  const [publishBusy, setPublishBusy] = React.useState(false);
  /** The draft moved under an open publish dialog — shown inline there. */
  const [publishNotice, setPublishNotice] = React.useState<string | null>(null);
  const canApproveReview = hasPermission(userPermissions, 'specs.approve_review');

  /** Discard asks first — a kit dialog that names who loses the draft. */
  const doDiscard = React.useCallback(() => setDiscardOpen(true), []);
  const confirmDiscard = React.useCallback(async () => {
    setDiscardBusy(true);
    let ok = false;
    try {
      // discard() toasts its own failures; the dialog never sticks on busy.
      ok = await draft.discard();
    } finally {
      setDiscardBusy(false);
      setDiscardOpen(false);
    }
    if (ok) onBack();
  }, [draft, onBack]);

  /** `asApprover`: a review-queue Approve — approving a request IS
   *  publishing it, so specs.approve_review may publish here (the server
   *  accepts the same key on the master save and the feed rows). */
  const doPublish = React.useCallback(
    async (asApprover = false) => {
      try {
        const start = await draft.publish(canDirectPublish || (asApprover && canApproveReview));
        setPublishNotice(null);
        if (start.kind === 'review') setPublishOpen(true);
        else if (start.kind === 'plan') setPublishPlan(start.plan);
      } catch (err) {
        toast(publishErrorMessage(err), true);
      }
    },
    [draft, canDirectPublish, canApproveReview, toast]
  );

  // Approve from the review queue: open the draft and the publish dialog.
  const loadedForPublish = draft.loadState === 'ready';
  const startedOpenPublishRef = React.useRef(false);
  React.useEffect(() => {
    if (!openPublish || !loadedForPublish || startedOpenPublishRef.current) return;
    startedOpenPublishRef.current = true;
    onPublishOpened?.();
    if (canEdit) setMode('edit');
    void doPublish(true);
  }, [openPublish, loadedForPublish, canEdit, doPublish, onPublishOpened]);

  /** Publish → the walk first; straight to the dialog when nothing differs. */
  const startPublishWalk = React.useCallback(() => {
    const pub = draft.master;
    if (!pub) {
      void doPublish(false);
      return;
    }
    // OURS = the draft as the canvas shows it (so every offset indexes the
    // document on screen), THEIRS = the published version; the plugin reads
    // the result inverted.
    const { before, after } = trackChangesSides({
      masterId,
      publishedCore: pub.coreContent || [],
      publishedLayers: pub.layers || [],
      draftCore: draft.currentBlocks(),
      draftLayers: draft.layers,
      layerId: variant.activeLayerId,
    });
    // On a variant tab the canvas renders the composed variant, not the
    // draft core — the hook reads what is ON SCREEN so offsets always line up.
    startWalk(
      before,
      after,
      () => void doPublish(false),
      () => setTrackOpen(false)
    );
  }, [draft, masterId, variant.activeLayerId, doPublish, startWalk]);
  const continueWalk = React.useCallback(
    () => finishWalk(() => void doPublish(false)),
    [finishWalk, doPublish]
  );

  const reloadUpdates = updates.reload;
  const confirmPublish = React.useCallback(async () => {
    if (!publishPlan) return;
    setPublishBusy(true);
    try {
      // Stale-dialog guard: the offers are re-read from the LIVE draft at
      // the moment of Publish. Different offers → nothing is published;
      // the dialog shows the new list first.
      const live = await draft.replanPublish();
      if (!live) throw new Error('The draft is no longer there');
      if (!sameOffers(publishPlan, live)) {
        setPublishPlan(live);
        setPublishNotice('The draft changed while this was open — check the new list.');
        return;
      }
      const published = await draft.commitPublish(live);
      setPublishNotice(null);
      setPublishPlan(null);
      // H5 — stay on the spec, in View of the new version.
      setMode('view');
      reloadUpdates();
      toast(`Published v${published.version}`);
    } catch (err) {
      // The batch is all-or-nothing: nothing changed, the draft is intact.
      toast(publishErrorMessage(err), true);
    } finally {
      setPublishBusy(false);
    }
  }, [publishPlan, draft, reloadUpdates, toast]);

  const masterWaiting = updates.countFor(null);

  const guardModuleChips = moduleInsert.guardLeave;
  const leave = React.useCallback(() => {
    // Chunks still in the placement tray are session state — say so before
    // they are lost, then fall through to the normal dirty-document guard.
    if (
      guardModuleChips(() => {
        if (draft.unsaved) setLeaveOpen(true);
        else onBack();
      })
    ) {
      return;
    }
    // A shared draft saves as you type — only work that has not reached the
    // server yet (offline edits, a write in flight, a pending rename) asks.
    if (draft.unsaved) {
      setLeaveOpen(true);
      return;
    }
    onBack();
  }, [draft.unsaved, onBack, guardModuleChips]);

  // Someone else discarded or published the shared draft: the document is
  // gone. Say so (if we were in it) and show the published truth in View —
  // nothing of ours is lost, the spec just moved on.
  const discardedElsewhere = draft.discardedElsewhere;
  const refreshDraft = draft.refresh;
  const handledDiscardRef = React.useRef(false);
  const modeNowRef = React.useRef(mode);
  modeNowRef.current = mode;
  React.useEffect(() => {
    if (!discardedElsewhere) {
      handledDiscardRef.current = false;
      return;
    }
    if (handledDiscardRef.current) return;
    handledDiscardRef.current = true;
    if (modeNowRef.current === 'edit') {
      toast('This draft was closed by a teammate — published or discarded');
    }
    setMode('view');
    void refreshDraft('ended-elsewhere');
  }, [discardedElsewhere, toast, refreshDraft]);

  // The draft's content changed outside the editor (an accepted module
  // update, an import...): the live document was reset server-side. Refresh
  // in place (same mode) so a fresh socket seeds from the fresh content.
  const resetElsewhere = draft.resetElsewhere;
  const handledResetRef = React.useRef(false);
  React.useEffect(() => {
    if (!resetElsewhere) {
      handledResetRef.current = false;
      return;
    }
    if (handledResetRef.current) return;
    handledResetRef.current = true;
    toast('Document refreshed with outside changes');
    void refreshDraft('reset');
  }, [resetElsewhere, toast, refreshDraft]);

  // The shell's nav clicks and the OS close command raise the SAME dialog.
  React.useEffect(() => {
    if (!exitGuardTriggerRef) return;
    exitGuardTriggerRef.current = { show: () => setLeaveOpen(true) };
    return () => {
      exitGuardTriggerRef.current = null;
    };
  }, [exitGuardTriggerRef]);

  /* ─── The toolbar (BlockEditor T1–T10, smart blocks removed) ─── */
  const master = draft.master;

  // Tab / window title: "<number> <title> · <project> · SpecForma".
  const recordName =
    [master?.csiNumber, master?.title].filter(Boolean).join(' ') || 'Untitled section';
  React.useEffect(() => {
    setRecordTitle(recordName);
    return () => setRecordTitle(null);
  }, [recordName]);

  // ─── Crumb rename (D4/T2.1) — every OTHER master, for the live section-
  // number uniqueness check (rename, and Save As below). Titles are never
  // checked (D5). ───
  const [allMasters, setAllMasters] = React.useState<SpecMaster[]>([]);
  React.useEffect(() => {
    let alive = true;
    void SpecService.listMasters().then((list) => {
      if (alive) setAllMasters(list);
    });
    return () => {
      alive = false;
    };
  }, []);
  const [renamingPart, setRenamingPart] = React.useState<'number' | 'title' | null>(null);
  const [renameDraft, setRenameDraft] = React.useState('');
  const [renameError, setRenameError] = React.useState<string | null>(null);
  const crumbNumberBtnRef = React.useRef<HTMLButtonElement>(null);
  const crumbTitleBtnRef = React.useRef<HTMLButtonElement>(null);
  const renameInputRef = React.useRef<HTMLInputElement>(null);
  // Both Enter and Esc route through the SAME onBlur commit path (rather
  // than deciding in onKeyDown) so an unmount-on-state-change never races a
  // native blur firing a second, stale commit — this flag is the only thing
  // that tells onBlur which one happened.
  const renameEscapingRef = React.useRef(false);

  const focusCrumbButton = React.useCallback((part: 'number' | 'title') => {
    requestAnimationFrame(() => {
      (part === 'number' ? crumbNumberBtnRef : crumbTitleBtnRef).current?.focus();
    });
  }, []);

  const cancelRename = React.useCallback(() => {
    const part = renamingPart;
    setRenamingPart(null);
    setRenameDraft('');
    setRenameError(null);
    if (part) focusCrumbButton(part);
  }, [renamingPart, focusCrumbButton]);

  const commitRename = React.useCallback(
    (part: 'number' | 'title') => {
      if (!master) return;
      const value = renameDraft.trim();
      if (!value) {
        cancelRename();
        return;
      }
      if (part === 'number') {
        const clash = csiConflict(allMasters, value, master.id);
        if (clash) {
          const msg = `Section ${value} is already used by "${clash.title}"`;
          setRenameError(msg);
          toast(msg, true);
          requestAnimationFrame(() => renameInputRef.current?.focus());
          return;
        }
        draft.rename({ csiNumber: value });
      } else {
        draft.rename({ title: value });
      }
      setRenamingPart(null);
      setRenameDraft('');
      setRenameError(null);
      focusCrumbButton(part);
    },
    [renameDraft, allMasters, master, draft, toast, cancelRename, focusCrumbButton]
  );

  // Save As always creates a brand-new master, so the check runs against
  // EVERY existing master — including the source's own number (D5: the
  // copy needs a number of its own).
  const saveAsCsiConflict = React.useMemo(
    () => (saveAsOpen ? csiConflict(allMasters, saveAsCsi) : null),
    [saveAsOpen, allMasters, saveAsCsi]
  );

  // Versions used to be their own modal; now they're the Versions tab of the
  // merged History panel. Newest first, same order the modal showed them.
  const historyVersions = React.useMemo<EditorHistoryVersion[]>(
    () =>
      [...(master?.versionHistory || [])].reverse().map((v) => ({
        version: v.version,
        savedBy: v.savedBy,
        savedAt: v.savedAt,
        blockCount: (v.snapshot?.coreContent || []).length,
      })),
    [master?.versionHistory]
  );
  const handleRevertVersion = React.useCallback(
    (version: number) => {
      if (!canEdit) return;
      leaveWalk();
      setTrackOpen(false);
      if (variant.inVariant) variant.switchTo(null);
      if (mode !== 'edit') enterEdit();
      setPendingRevert(version);
    },
    [canEdit, leaveWalk, variant, mode, enterEdit]
  );
  // Safety net: the lane belongs to the Office Master tab — a variant tab
  // never receives a revert.
  React.useEffect(() => {
    if (variant.inVariant) cancelRevert();
  }, [variant.inVariant, cancelRevert]);
  // Open the lane once the canvas IS the shared draft — never a stand-in.
  const startRevert = revertReview.start;
  React.useEffect(() => {
    if (pendingRevert === null) return;
    if (mode !== 'edit') {
      // Edit was refused (live editing blocked) — nothing opens.
      if (liveBlocked) setPendingRevert(null);
      return;
    }
    if (variant.inVariant || !draft.collab || !editorBoundTo(editor, draft.collab.ydoc)) return;
    const v = pendingRevert;
    setPendingRevert(null);
    const entry = (master?.versionHistory || []).find((h) => h.version === v);
    if (!entry) {
      toast(`Version ${v} is no longer in History.`, true);
      return;
    }
    const opened = startRevert(entry.snapshot?.coreContent || [], `version ${v}`, `v${v}`);
    if (opened === 'nothing') toast(`The draft already matches version ${v}.`);
  }, [
    pendingRevert,
    mode,
    liveBlocked,
    variant.inVariant,
    draft.collab,
    editor,
    master?.versionHistory,
    startRevert,
    toast,
  ]);
  const applyRevertReview = revertReview.apply;
  const [revertBusy, setRevertBusy] = React.useState(false);
  const applyRevert = React.useCallback(async () => {
    const res = applyRevertReview();
    if (!res) {
      toast('The document is not ready — try Apply revert again.', true);
      return;
    }
    if (res.accepted === 0) {
      toast('Nothing reverted — the draft is unchanged.');
      return;
    }
    const note = revertHistoryNote(res.short, res.full);
    setRevertBusy(true);
    try {
      // The edit is already in the shared draft; flush its mirror now.
      await draft.save();
      await draft.noteHistory(note);
      toast(`${note} — it is in the draft. Publish when ready.`);
    } finally {
      setRevertBusy(false);
    }
  }, [applyRevertReview, draft, toast]);

  const toolbarItems = React.useMemo<ToolbarItem[]>(() => {
    if (!master) return [];
    return [
      {
        id: 'undo-redo',
        label: 'Undo and redo',
        core: true,
        section: 'editing',
        node: <UniversalHistory timeline={undoTimeline} editor={editor} />,
      },
      {
        id: 'paragraph',
        label: 'Paragraph',
        core: true,
        section: 'editing',
        node: <ParagraphGroup editor={editor} canEdit={editing} />,
      },
      {
        id: 'marks',
        label: 'Text formatting',
        section: 'editing',
        node: <InlineMarks editor={editor} canEdit={editing} />,
      },
      {
        id: 'find',
        label: 'Find and replace',
        section: 'editing',
        node: (
          <ToolbarButton
            icon={Search}
            label="Find & replace"
            tooltip="Ctrl+F · Ctrl+H"
            aria-label="Find and replace"
            disabled={!editor}
            onClick={() => {
              if (!editor) return;
              openFind(editor.state, editor.view.dispatch.bind(editor.view));
            }}
          />
        ),
      },
      {
        id: 'zoom',
        label: 'Zoom',
        // CORE, exactly like Master Formas' zoom (forma-studio/Studio.tsx).
        // Without it the control is only "pinned by default": a user who has
        // ever customised this toolbar has a SAVED pin list, and any item
        // missing from that list is hidden (editor-chrome/Toolbar.tsx). That is
        // why zoom read as absent even though it shipped in v2.19.0 — the
        // persisted zoom preference was working the whole time, with no visible
        // control to drive it (Phase 1 item 6).
        core: true,
        section: 'editing',
        node: (
          <ZoomControl
            value={Math.round(zoom * 100)}
            onChange={(pct) => setZoomPersisted(pct / 100)}
            min={Math.round(ZOOM_MIN * 100)}
            max={Math.round(ZOOM_MAX * 100)}
          />
        ),
      },
      {
        id: 'view',
        label: 'View menu',
        section: 'editing',
        node: (
          <ToolbarButton
            label="View"
            active={!!diffBlocks}
            aria-haspopup="menu"
            aria-expanded={!!viewMenu}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setViewMenu((v) => (v ? null : { left: r.left, bottom: r.bottom }));
            }}
            tooltip="Display options"
          />
        ),
      },
      {
        id: 'history',
        label: 'History — versions and edits',
        core: true,
        section: 'document',
        node: (
          <EditorHistoryPanel
            entries={master.editHistory || []}
            versions={historyVersions}
            onRevert={handleRevertVersion}
            canRevert={canEdit}
          />
        ),
      },
      {
        id: 'save-as',
        label: 'Save as a new master',
        core: true,
        section: 'document',
        node: (
          <ToolbarButton
            icon={CopyPlus}
            label="Save As"
            variant="outline"
            onClick={() => {
              setSaveAsTitle((master.title || '') + ' (Copy)');
              setSaveAsCsi('');
              setSaveAsOpen(true);
            }}
          />
        ),
      },
      // Discard lives with the draft — Edit mode only. Publish moved to the
      // master header (the one primary call to action, beside the mode).
      ...(editing
        ? [
            {
              id: 'discard',
              label: 'Discard draft',
              core: true,
              section: 'document' as const,
              node: (
                <ToolbarButton
                  icon={Trash2}
                  label="Discard"
                  variant="destructive"
                  onClick={doDiscard}
                  disabled={!draft.draftExists}
                  tooltip="Throw the draft away for everyone"
                />
              ),
            },
          ]
        : []),
    ];
  }, [
    master,
    canEdit,
    editor,
    undoTimeline,
    editing,
    zoom,
    viewMenu,
    diffBlocks,
    historyVersions,
    handleRevertVersion,
    draft.draftExists,
    doDiscard,
  ]);

  /* ─── States ─── */
  if (draft.loadState === 'loading') {
    return (
      <div className="dsg-root dsg-loading sps-state-root" aria-busy="true">
        <span className="sr-only">Opening the spec master…</span>
        <div className="dsg-skeleton-sheet" />
      </div>
    );
  }

  if (draft.loadState !== 'ready' || !master) {
    const notFound = draft.loadState === 'not-found';
    return (
      <div className="dsg-root dsg-error-root sps-state-root">
        <EmptyState
          title={
            notFound
              ? 'This spec master could not be found'
              : 'This spec master could not be opened'
          }
          subtitle={
            notFound ? 'It may have been deleted.' : draft.error || 'Try again in a moment.'
          }
          action={
            notFound
              ? { label: 'Back to Master Specs', onClick: onBack }
              : { label: 'Try again', onClick: () => void draft.reload() }
          }
        />
      </div>
    );
  }

  const specNumber = master.csiNumber || '';
  const specName = master.title || 'Untitled section';

  return (
    <ViewModeContext.Provider value={vm}>
      <div
        // Typing on the paper in View: no text changes (the editor is read-only);
        // the keystroke points at Edit instead.
        onKeyDownCapture={(e) => {
          if (editing || e.ctrlKey || e.metaKey || e.altKey) return;
          if (!(e.target as HTMLElement).closest?.('.ProseMirror')) return;
          if (e.key.length === 1 || e.key === 'Enter') nudgeEdit();
        }}
        className={[
          'dsg-root',
          'sps-root',
          viewOpts.showParagraphNumbers ? '' : 'sps-no-numbers',
          viewOpts.showExcluded ? '' : 'sps-hide-excluded',
          viewOpts.showGhosts ? '' : 'sps-hide-ghosts',
          viewOpts.showProvenance ? '' : 'sps-hide-prov',
          variant.inVariant ? 'sps-in-variant' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <header className="dsg-topbar">
          <div className="dsg-idrow">
            <button type="button" className="dsg-back" onClick={leave}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Master Specs
            </button>
            <nav className="dsg-crumb" aria-label="Breadcrumb">
              {canEdit && renamingPart === 'number' ? (
                <span className="sps-crumb-edit-wrap">
                  <input
                    ref={renameInputRef}
                    autoFocus
                    aria-label="Section number"
                    aria-invalid={!!renameError}
                    aria-describedby={renameError ? 'sps-rename-err' : undefined}
                    className="sfs-rename-input"
                    value={renameDraft}
                    onChange={(e) => {
                      setRenameDraft(e.target.value);
                      setRenameError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      if (e.key === 'Escape') {
                        renameEscapingRef.current = true;
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    onBlur={() => {
                      if (renameEscapingRef.current) {
                        renameEscapingRef.current = false;
                        cancelRename();
                        return;
                      }
                      commitRename('number');
                    }}
                  />
                  {renameError && (
                    <span
                      id="sps-rename-err"
                      className="sfs-rename-error"
                      role="alert"
                      aria-live="assertive"
                    >
                      {renameError}
                    </span>
                  )}
                </span>
              ) : canEdit ? (
                <button
                  ref={crumbNumberBtnRef}
                  type="button"
                  className="dsg-crumb-project sfs-crumb-btn"
                  title="Rename this section's number"
                  onClick={() => {
                    setRenameDraft(specNumber);
                    setRenameError(null);
                    setRenamingPart('number');
                  }}
                >
                  {specNumber || 'Master'}
                </button>
              ) : (
                <span className="dsg-crumb-project">{specNumber || 'Master'}</span>
              )}
              <span className="dsg-crumb-sep" aria-hidden="true">
                ·
              </span>
              {canEdit && renamingPart === 'title' ? (
                <input
                  ref={renameInputRef}
                  autoFocus
                  aria-label="Section title"
                  className="sfs-rename-input"
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') {
                      renameEscapingRef.current = true;
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  onBlur={() => {
                    if (renameEscapingRef.current) {
                      renameEscapingRef.current = false;
                      cancelRename();
                      return;
                    }
                    commitRename('title');
                  }}
                />
              ) : canEdit ? (
                <button
                  ref={crumbTitleBtnRef}
                  type="button"
                  className="dsg-crumb-spec sfs-crumb-btn"
                  title="Rename this section"
                  onClick={() => {
                    setRenameDraft(specName);
                    setRenamingPart('title');
                  }}
                >
                  <strong>{specName}</strong>
                </button>
              ) : (
                <span className="dsg-crumb-spec">
                  <strong>{specName}</strong>
                </span>
              )}
              {/* ONE "·" between each part, all inside the crumb:
                <number> · <Title> · Office Master · v<N>. */}
              <span className="dsg-crumb-sep" aria-hidden="true">
                ·
              </span>
              {/* The Office Master is the page — the implicit top state. In a
                variant this is the way back (as is the context line). */}
              {variant.inVariant ? (
                <button
                  type="button"
                  className="sps-master-crumb sfs-crumb-btn"
                  onClick={() => variant.switchTo(null)}
                  title="Back to the office master"
                >
                  Office Master
                  {masterWaiting > 0 && <span className="sps-count-pill">{masterWaiting}</span>}
                </button>
              ) : (
                <span
                  className="sps-master-crumb"
                  title={
                    masterWaiting > 0
                      ? `${masterWaiting} module update${masterWaiting === 1 ? '' : 's'} waiting`
                      : "The firm's master — the text every variant starts from"
                  }
                >
                  Office Master
                  {masterWaiting > 0 && (
                    <span className="sps-count-pill">
                      {masterWaiting}
                      <span className="sr-only"> module updates waiting</span>
                    </span>
                  )}
                </span>
              )}
              <span className="dsg-crumb-sep" aria-hidden="true">
                ·
              </span>
              <span className="dsg-crumb-project">v{master.version ?? 0}</span>
            </nav>
            <span className="sps-chip sps-chip-draft">
              {masterStatusChip(master.status, editing)}
            </span>
            {draft.prStatus === 'pending' && (
              <span className="sps-chip sps-chip-warn">PR pending</span>
            )}
            {draft.prStatus === 'rejected' && (
              <span className="sps-chip sps-chip-danger">PR rejected</span>
            )}
            <div className="dsg-idrow-status">
              {editing && <SaveIndicator state={draft.saveState} />}
              {editing && <PeerChips peers={draft.peers} />}
              {canExport && draft.master && (
                <button type="button" className="dsg-btn" onClick={() => setExportOpen(true)}>
                  Export
                </button>
              )}
              {canExport && draft.master && draft.draftInProgress && (
                <span className="dsg-crumb-project">Exports the published version.</span>
              )}
              {editing ? (
                <>
                  <button type="button" className="dsg-btn" onClick={() => setMode('view')}>
                    View published
                  </button>
                  {/* While the walk is up, its Continue is the one primary. */}
                  {!walk && !reverting && (
                    <button
                      type="button"
                      className="dsg-primary"
                      onClick={startPublishWalk}
                      disabled={draft.prStatus === 'pending' || publishBusy}
                    >
                      {canDirectPublish
                        ? 'Publish'
                        : draft.prStatus === 'pending'
                          ? 'PR pending…'
                          : 'Submit for review'}
                    </button>
                  )}
                </>
              ) : canEdit ? (
                <Hint text={liveBlockedMessage}>
                  <button
                    ref={editBtnRef}
                    type="button"
                    className="dsg-primary"
                    onClick={enterEdit}
                    onAnimationEnd={(e) => e.currentTarget.classList.remove('sps-edit-nudge')}
                    // aria-disabled, not disabled: the tooltip with the reason
                    // must still show on hover and focus.
                    aria-disabled={liveBlocked ? true : undefined}
                  >
                    Edit
                  </button>
                </Hint>
              ) : null}
            </div>
          </div>

          {mode === 'view' && draft.draftInProgress && (
            <div className="sps-draft-line" role="status">
              <span>Draft in progress — {draftPresenceLabel(draft.peers)}</span>
              {canEdit && (
                <>
                  <span aria-hidden="true">·</span>
                  <button type="button" className="sps-link-btn" onClick={enterEdit}>
                    Open draft
                  </button>
                </>
              )}
            </div>
          )}

          <VariantBar
            layers={variant.layers}
            activeLayerId={variant.activeLayerId}
            canEdit={editing}
            canDelete={canDelete && editing}
            editing={editing}
            masterVersion={master.version ?? 0}
            onSwitch={variant.switchTo}
            switchLocked={reverting ? 'Finish or cancel the revert first' : null}
            onCreate={(name) => variant.createVariant(name)}
            onDuplicate={(layerId, name) => variant.duplicateVariant(layerId, name)}
            onRequestDelete={(layer) => setDeleteVariantTarget(layer.id)}
            pendingUpdates={updates.countFor}
          />

          <div
            ref={toolbarRef}
            // The publish walk is read-only: same muted, aria-disabled look as
            // View mode, and presses are swallowed until Continue / leave.
            aria-disabled={walk || reverting ? true : undefined}
            className={walk || reverting ? 'sps-vm-inert' : undefined}
            title={walk || reverting ? vm.reason : undefined}
            onClickCapture={
              walk || reverting
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                : undefined
            }
          >
            <DesignerToolbar
              items={toolbarItems}
              panels={panelEntries}
              onPanelClick={onPanelClick}
              onPanelDragStart={setPanelDragId}
              onPanelDragEnd={onPanelDragEnd}
              dockLocked={dockLocked}
              onToggleDockLocked={toggleDockLocked}
              onResetLayout={() => dockHandleRef.current?.resetLayout()}
              onSetFirmDefault={setFirmDefault}
              canSetFirmDefault={canSetFirmDefault}
              userId={userId}
              storageKeyPrefix="sf.specStudioToolbar.v1."
            />
          </div>
        </header>

        {walk && (
          <ReviewBar
            lane="master"
            mode="walk"
            index={walker.index}
            total={walk.length}
            onPrev={walker.prev}
            onNext={walker.next}
            onFinish={continueWalk}
            onLeave={leaveWalk}
            canDecide={false}
            busy={publishBusy}
            note="Your changes since the published version"
          />
        )}
        {revertReview.session && (
          <ReviewBar
            lane="revert"
            mode="review"
            title={revertLaneTitle(revertReview.session.target)}
            index={revertReview.walker.index}
            total={revertReview.items.length}
            decision={
              revertReview.currentId ? revertReview.decisions[revertReview.currentId] : null
            }
            onPrev={revertReview.walker.prev}
            onNext={revertReview.walker.next}
            onAccept={() => {
              if (!revertReview.currentId) return;
              revertReview.decide(revertReview.currentId, 'accept');
              revertReview.walker.next();
            }}
            onReject={() => {
              if (!revertReview.currentId) return;
              revertReview.decide(revertReview.currentId, 'reject');
              revertReview.walker.next();
            }}
            onAcceptAll={revertReview.acceptAll}
            onRejectAll={revertReview.rejectAll}
            onFinish={() => void applyRevert()}
            onLeave={cancelRevert}
            canDecide
            remaining={revertReview.remaining}
            busy={revertBusy}
          />
        )}
        {!walk && !reverting && !variantBarClosed && variantReview && variant.activeLayerId && (
          <ReviewBar
            lane="master"
            mode="review"
            index={variantWalker.index}
            total={variantReview.items.length}
            remaining={updates.pendingTotal}
            onPrev={variantWalker.prev}
            onNext={variantWalker.next}
            onAccept={() =>
              variantCurrent && void decideVariantBlock(variantCurrent.blockId, 'accepted')
            }
            onReject={() =>
              variantCurrent && void decideVariantBlock(variantCurrent.blockId, 'rejected')
            }
            onAcceptAll={() => void acceptAllVariant()}
            onFinish={() => setVariantBarClosed(true)}
            onLeave={() => setVariantBarClosed(true)}
            canDecide={canAcceptUpdates}
            busy={updates.busy}
            note="Updates from the office master"
          />
        )}

        <div className="dsg-body">
          <main className="dsg-main">
            <SpecStudioWorkspace
              isDark={isDark}
              userId={userId || 'anon'}
              locked={dockLocked}
              editor={editor}
              canEdit={editing}
              outlineBlocks={outlineBlocks}
              onOutlineJump={jumpToDiffRow}
              floatingTools={floatingTools}
              onCloseFloating={closeFloating}
              onDockFloating={dockFloating}
              dropdown={dropdown}
              onCloseDropdown={() => setDropdown(null)}
              onFloatDropdown={floatTool}
              onDockPanelsChange={setDockPanels}
              onDockReady={(h) => {
                dockHandleRef.current = h;
                setDockReady(true);
              }}
              updates={updatesPanel}
              modules={modulesPanel}
              onSaveAsModule={openSaveAsModule}
              canSaveAsModule={canCreateModule || canEditModules}
              canvas={
                diffBlocks ? (
                  <TrackChangesView blocks={diffBlocks} publishedVersion={master.version ?? 0} />
                ) : variant.ready ? (
                  <SpecStudioCanvas
                    docKey={variant.docKey}
                    blocks={variant.docBlocks}
                    // The Office Master tab IS the shared document; a variant
                    // tab composes from the published text and writes layers.
                    collab={variant.inVariant ? null : draft.collab}
                    specNumber={specNumber}
                    specName={specName}
                    zoom={zoom}
                    // Editing waits for the shared document (synced + seeded),
                    // so nothing typed can land outside it.
                    editable={editing && !walk && !reverting && !!draft.collab}
                    canDelete={canDelete}
                    onChange={onCanvasChange}
                    onSave={handleSave}
                    onNotice={toast}
                    onEditorReady={setEditor}
                    onZoomDelta={(step) => setZoomPersisted(zoom + step)}
                    variant={
                      variant.inVariant
                        ? {
                            getView: variant.getView,
                            // 'update' is Phase 3's tag and belongs to the feed,
                            // not the layer — everything else is the variant's own.
                            onTag: (kind, blockId, rect) =>
                              kind === 'update'
                                ? openUpdatesFor(blockId, rect)
                                : editing || kind === 'stale'
                                  ? variant.onTag(kind, blockId, rect)
                                  : nudgeEdit(),
                            onGate: variant.onGate,
                            getPending: getPendingUpdateIds,
                          }
                        : null
                    }
                    variantMenu={
                      variant.inVariant && variant.activeLayer
                        ? {
                            clientName: variant.activeLayer.name || 'Variant',
                            hide: guardHandler(vm, variant.hideSelection),
                            restore: guardHandler(vm, (id: string) => variant.setGhost(id, false)),
                            revert: guardHandler(vm, variant.revert),
                            canDecide: canAcceptUpdates,
                            acceptUpdate: (id: string) => void updates.decideBlock(id, 'accepted'),
                            rejectUpdate: (id: string) => void updates.decideBlock(id, 'rejected'),
                          }
                        : null
                    }
                    moduleMenu={{
                      canCreate: canCreateModule || canEditModules,
                      saveAsModule: openSaveAsModule,
                      selectInsertion: moduleInsert.selectInsertion,
                      removeInsertion: guardHandler(vm, moduleInsert.requestRemoveInsertion),
                    }}
                  />
                ) : (
                  <div className="dsg-skeleton-sheet" aria-hidden="true" />
                )
              }
            />
          </main>
          {walk && (
            <ReviewPanel
              title="Changes in this draft"
              items={walk}
              currentId={walker.current?.id ?? null}
              onJump={walker.setCurrentId}
              invert
              showDecisions={false}
              emptyTitle="This draft matches the published version."
            />
          )}
          {revertReview.session && (
            <ReviewPanel
              title={`Changes back to ${revertReview.session.target}`}
              items={revertReview.items}
              decisions={revertReview.decisions}
              currentId={revertReview.currentId}
              onJump={revertReview.walker.setCurrentId}
              emptyTitle={`The draft already matches ${revertReview.session.target}.`}
            />
          )}
        </div>

        {/* Inserting a module: the drop line, and the two confirmations. */}
        {moduleInsert.overlay}

        {/* Save as module — the selection is frozen the moment this opens. */}
        {moduleCapture && (
          <SaveAsModuleDialog
            open={!!moduleCapture}
            onOpenChange={(o) => {
              if (!o) setModuleCapture(null);
            }}
            blocks={moduleCapture.blocks}
            selectedIds={moduleCapture.ids}
            sourceSpecId={masterId}
            sourceCsi={specNumber}
            userName={userName}
            canCreate={canCreateModule}
            canEditModules={canEditModules}
            toast={toast}
            onContinue={(next) => {
              setModuleCapture(null);
              openModuleBuilder(next);
            }}
          />
        )}

        {/* The module builder, over the Studio. The spec underneath is untouched
          — a module is a copy, and nothing here writes back to it. */}
        {moduleBuilder && (
          <div className="sps-mod-overlay">
            <ModuleBuilder
              draft={moduleBuilder}
              userName={userName}
              userId={userId}
              isDark={isDark}
              userPermissions={userPermissions}
              toast={toast}
              onClose={() => setModuleBuilder(null)}
            />
          </div>
        )}

        {/* Find & replace — Ctrl+F / Ctrl+H, anchored under the toolbar. */}
        {editor && !diffBlocks && (
          <FindReplace editor={editor} canEdit={editing} anchorRef={toolbarRef} />
        )}

        {/* ── View menu ── */}
        {viewMenu && (
          <ViewMenu
            anchor={viewMenu}
            opts={viewOpts}
            inVariant={variant.inVariant}
            canTrack={mode === 'edit'}
            trackChanges={!!diffBlocks}
            onToggle={(k) => setViewOpts((p) => ({ ...p, [k]: !p[k] }))}
            onToggleTrackChanges={() => {
              toggleTrackChanges();
              setViewMenu(null);
            }}
            onClose={() => setViewMenu(null)}
          />
        )}

        {/* ── Save As ── */}
        {saveAsOpen && (
          <Modal
            title="Save as a new spec master"
            sub="The copy starts at v1 with its own history. This master is left untouched."
            onClose={() => setSaveAsOpen(false)}
          >
            <label className="sps-field-label" htmlFor="sps-saveas-csi">
              Section number
            </label>
            <input
              id="sps-saveas-csi"
              className="dsg-input"
              value={saveAsCsi}
              onChange={(e) => setSaveAsCsi(e.target.value)}
              placeholder="27 41 00"
              aria-invalid={!!saveAsCsiConflict}
              aria-describedby={saveAsCsiConflict ? 'sps-saveas-csi-err' : undefined}
            />
            {saveAsCsiConflict && (
              <span
                id="sps-saveas-csi-err"
                className="sfs-rename-error"
                role="alert"
                aria-live="polite"
              >
                {`Section ${saveAsCsi.trim()} is already used by "${saveAsCsiConflict.title}"`}
              </span>
            )}
            <label className="sps-field-label" htmlFor="sps-saveas">
              New title
            </label>
            <input
              id="sps-saveas"
              className="dsg-input"
              value={saveAsTitle}
              onChange={(e) => setSaveAsTitle(e.target.value)}
              placeholder="Audio-Video Systems"
            />
            <div className="dsg-desk-actions">
              <button type="button" className="dsg-btn" onClick={() => setSaveAsOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="dsg-primary"
                disabled={!saveAsTitle.trim() || !saveAsCsi.trim() || !!saveAsCsiConflict}
                onClick={() => {
                  void draft.saveAs(saveAsTitle, saveAsCsi).then((saved: SpecMaster | null) => {
                    setSaveAsOpen(false);
                    if (saved && onSavedAs) onSavedAs(saved.id);
                  });
                }}
              >
                Save as new master
              </button>
            </div>
          </Modal>
        )}

        {/* ── Submit for review ── */}
        {publishOpen && (
          <Modal
            title="Submit for review"
            sub="An administrator reviews your changes before they merge into the published master."
            onClose={() => setPublishOpen(false)}
          >
            <label className="sps-field-label" htmlFor="sps-prmsg">
              What changed?
            </label>
            <input
              id="sps-prmsg"
              className="dsg-input"
              value={publishMessage}
              onChange={(e) => setPublishMessage(e.target.value)}
              placeholder="Updated Part 2 product list"
            />
            <div className="dsg-desk-actions">
              <button type="button" className="dsg-btn" onClick={() => setPublishOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="dsg-primary"
                onClick={() => {
                  void draft.submitForReview(publishMessage).then(() => {
                    setPublishOpen(false);
                    setPublishMessage('');
                  });
                }}
              >
                Submit for review
              </button>
            </div>
          </Modal>
        )}

        {/* ── "Core has changed" side-by-side (R3 §2.3) ── */}
        {variant.compare && variant.activeLayer && (
          <VariantStaleCompare
            core={variant.compare.core}
            variant={variant.compare.variant}
            clientName={variant.activeLayer.name || 'This variant'}
            anchor={variant.compare.anchor}
            onRevert={guardHandler(vm, () => variant.revert(variant.compare!.blockId))}
            onKeep={guardHandler(vm, () => variant.acknowledgeStale(variant.compare!.blockId))}
            onClose={variant.closeCompare}
          />
        )}

        {/* ── Delete a variant — destructive, so it asks and it is permission-gated
             (DESIGN §4; only reachable at all when `canDelete`). ── */}
        {deleteVariantTarget && (
          <Modal
            title={`Delete the ${
              variant.layers.find((l) => l.id === deleteVariantTarget)?.name || 'this'
            } variant?`}
            sub="This variant's added blocks, overrides and hidden rows on this master are deleted. The master, and every other variant, are untouched."
            onClose={() => setDeleteVariantTarget(null)}
          >
            <div className="dsg-desk-actions">
              <button
                type="button"
                className="dsg-btn"
                onClick={() => setDeleteVariantTarget(null)}
              >
                Keep the variant
              </button>
              <button
                type="button"
                className="dsg-btn sps-danger-btn"
                onClick={() => {
                  variant.deleteVariant(deleteVariantTarget);
                  setDeleteVariantTarget(null);
                }}
              >
                Delete variant
              </button>
            </div>
          </Modal>
        )}

        {/* ── Discard the shared draft — names who loses it (SPEC_WORKFLOW §5) ── */}
        <ConfirmDialog
          open={discardOpen}
          onOpenChange={(o) => {
            if (!o) setDiscardOpen(false);
          }}
          title="Discard draft"
          description={discardConfirmBody(draft.peers)}
          confirmLabel="Discard draft"
          cancelLabel="Keep the draft"
          busy={discardBusy}
          onConfirm={() => void confirmDiscard()}
        />

        {canExport && draft.master && (
          <ExportMastersDialog
            open={exportOpen}
            onOpenChange={setExportOpen}
            masters={[draft.master]}
            hasDraft={draft.draftInProgress}
            userPermissions={userPermissions}
            userName={userName}
            toast={toast}
          />
        )}
        <DraftPendingDialog
          open={draftNoticeOpen && mode === 'view'}
          who={draft.peers.length ? draftPresenceLabel(draft.peers) : null}
          onViewPublished={() => setDraftNoticeOpen(false)}
          onOpenDraft={() => {
            setDraftNoticeOpen(false);
            enterEdit();
          }}
        />

        {/* ── Publish — lists the variants it will offer changes to (Tweaks 35) ── */}
        {publishPlan && (
          <PublishDialog
            // A re-planned publish is a new list of offers: a fresh dialog.
            key={publishPlan.entries.map((e) => e.id).join(',') || 'none'}
            plan={publishPlan}
            notice={publishNotice}
            busy={publishBusy}
            onCancel={() => {
              setPublishPlan(null);
              setPublishNotice(null);
            }}
            onPublish={() => void confirmPublish()}
          />
        )}

        {/* ── Exit guard — fires ONLY when dirty (DESIGN §4) ── */}
        {leaveOpen && (
          <Modal
            title="You have unsaved changes"
            sub="Save the draft, discard it, or stay here and keep working."
            onClose={() => setLeaveOpen(false)}
          >
            <div className="dsg-desk-actions">
              <button
                type="button"
                className="dsg-btn"
                onClick={() => {
                  setLeaveOpen(false);
                  onCancelExit?.();
                }}
              >
                Keep editing
              </button>
              <button
                type="button"
                className="dsg-btn sps-danger-btn"
                onClick={() => {
                  setLeaveOpen(false);
                  doDiscard();
                }}
              >
                Discard draft
              </button>
              <button
                type="button"
                className="dsg-primary"
                onClick={() => {
                  setLeaveOpen(false);
                  // A failed save is toasted and keeps the user here — never
                  // leave with work that did not reach the server.
                  void draft.save().then((ok) => {
                    if (ok) onBack();
                  });
                }}
              >
                Save and leave
              </button>
            </div>
          </Modal>
        )}
      </div>
    </ViewModeContext.Provider>
  );
}

/* ─── The View menu — portalled, mousedown-outside + Esc (DESIGN §4) ─── */

function ViewMenu({
  anchor,
  opts,
  inVariant,
  canTrack,
  trackChanges,
  onToggle,
  onToggleTrackChanges,
  onClose,
}: {
  anchor: { left: number; bottom: number };
  opts: ViewOpts;
  inVariant: boolean;
  /** Track changes compares a DRAFT — offered in Edit mode only (§6). */
  canTrack: boolean;
  trackChanges: boolean;
  onToggle: (key: keyof ViewOpts) => void;
  onToggleTrackChanges: () => void;
  onClose: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);
  React.useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, []);

  const width = 288;
  const left = Math.min(anchor.left, window.innerWidth - width - 8);

  return (
    <div
      ref={ref}
      className="dsg-menu sps-menu"
      role="menu"
      aria-label="Display options"
      style={{ position: 'fixed', left, top: anchor.bottom + 4, width }}
    >
      <div className="sps-menu-label" aria-hidden="true">
        Visibility
      </div>
      <button
        type="button"
        role="menuitemcheckbox"
        aria-checked={opts.showExcluded}
        className={`dsg-menu-item${opts.showExcluded ? ' is-current' : ''}`}
        onClick={() => onToggle('showExcluded')}
      >
        Show blocks skipped at export
      </button>
      <button
        type="button"
        role="menuitemcheckbox"
        aria-checked={opts.showParagraphNumbers}
        className={`dsg-menu-item${opts.showParagraphNumbers ? ' is-current' : ''}`}
        onClick={() => onToggle('showParagraphNumbers')}
      >
        Paragraph numbers
      </button>
      {inVariant && (
        <>
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={opts.showGhosts}
            className={`dsg-menu-item${opts.showGhosts ? ' is-current' : ''}`}
            onClick={() => onToggle('showGhosts')}
            title="Blocks this variant hid stay on the sheet as dashed stubs"
          >
            Ghost rows (hidden blocks)
          </button>
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={opts.showProvenance}
            className={`dsg-menu-item${opts.showProvenance ? ' is-current' : ''}`}
            onClick={() => onToggle('showProvenance')}
            title="The small ‹Client› / ⇄ / Overridden tags. The coloured edge stays either way."
          >
            Provenance tags
          </button>
        </>
      )}
      {canTrack && (
        <>
          <div className="dsg-menu-sep" role="separator" />
          <div className="sps-menu-label" aria-hidden="true">
            Review
          </div>
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={trackChanges}
            className={`dsg-menu-item${trackChanges ? ' is-current' : ''}`}
            onClick={onToggleTrackChanges}
          >
            Track changes
          </button>
        </>
      )}
    </div>
  );
}

export default SpecStudio;
